import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ROWS_PER_PAGE } from 'projects/admin/src/app/shared/constants';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { DataScopeModel } from 'projects/core/src/lib/models/data-scope.model';

class CustomBusinessunitModel extends BusinessunitModel {
  control_enabled: UntypedFormControl = new UntypedFormControl(false); // tslint:disable-line: variable-name
}

@Component({
  selector: 'app-data-scope-businessunits',
  templateUrl: './data-scope-businessunits.component.html',
  styleUrls: ['./data-scope-businessunits.component.scss']
})
export class DataScopeBusinessunitsComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() scope: DataScopeModel = null;
  @Output() setEdit = new EventEmitter<string>();
  @Output() entryCountChange = new EventEmitter<number>();

  public loading = false;
  public groups: Array<BusinessunitModel> = [];
  public orphans: Array<{id: number, name: string}> = [];
  public rowsPerPage = ROWS_PER_PAGE;

  public editRight = 'admin.datascope.edit';

  public form = this.fb.group({});
  private initializedId: number = null;
  private countSubscription: Subscription = null;

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  ngOnDestroy(): void {
    if (this.countSubscription !== null) {
      this.countSubscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    this.initGroups();
    this.setBusinessunits();
    this.setOrphans();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        ids: this.getBusinessunitIds()
      };
      this.api.saveDataScopeEntries(this.scope.id, 'businessunit', payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.scope.name }), this.transloco.translate('common.saved'));
          this.scope.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  hasEmptyEntries(): boolean {
    if (this.scope === null || this.scope.businessunit === undefined) {
      return false;
    }
    if (this.inEdit === true) {
      return this.getBusinessunitIds().length === 0;
    }
    const entries = this.scope.businessunit.entries;
    return !entries || entries.length === 0;
  }

  protected startEdit(): void {
    if (this.auth.hasRight(this.editRight)) {
      if (this.scope !== null) {
        this.form.patchValue({});
      }
      this.setEdit.emit('businessunits');
      super.startEdit();
      this.ui.setFormFocus();
    }
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
    this.setBusinessunits();
    this.setOrphans();
  }

  private initGroups(): void {
    if (this.scope === null || this.scope.businessunit === undefined) {
      return;
    }
    if (this.initializedId === this.scope.id) {
      return;
    }
    this.initializedId = this.scope.id;
    this.form = this.fb.group({});
    this.groups = [];
    const selectable = this.scope.businessunit.selectable || [];
    selectable.forEach((group: any) => {
      const groupModel = new BusinessunitModel().deserialize(group);
      const children: Array<CustomBusinessunitModel> = [];
      (group.businessunits || []).forEach((child: any) => {
        const leaf = new CustomBusinessunitModel().deserialize(child);
        this.form.addControl('bu_' + leaf.id + '_enabled', leaf.control_enabled);
        children.push(leaf);
      });
      children.sort((a: CustomBusinessunitModel, b: CustomBusinessunitModel) => {
        return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
      });
      groupModel.businessunits = children;
      this.groups.push(groupModel);
    });
    this.watchEntryCount();
  }

  private setBusinessunits(): void {
    if (this.scope !== null && this.scope.businessunit !== undefined) {
      this.groups.forEach((group: BusinessunitModel) => {
        (group.businessunits || []).forEach((child: CustomBusinessunitModel) => {
          const found = this.scope.businessunit.entries?.find((actual: string | number) => {
            return actual === child.id;
          });
          child.control_enabled.setValue(found !== undefined);
        });
      });
    }
  }

  private setOrphans(): void {
    if (this.scope === null || this.scope.businessunit === undefined) {
      this.orphans = [];
      return;
    }
    this.orphans = this.scope.businessunit.orphans || [];
  }

  private watchEntryCount(): void {
    if (this.countSubscription !== null) {
      this.countSubscription.unsubscribe();
    }
    this.countSubscription = this.form.valueChanges.subscribe(() => {
      this.entryCountChange.emit(this.getBusinessunitIds().length);
    });
  }

  private getBusinessunitIds(): Array<number> {
    const ids: Array<number> = [];
    this.groups.forEach((group: BusinessunitModel) => {
      (group.businessunits || []).forEach((child: CustomBusinessunitModel) => {
        if (child.control_enabled.value === true) {
          ids.push(child.id);
        }
      });
    });
    this.orphans.forEach((orphan: {id: number, name: string}) => {
      ids.push(orphan.id);
    });
    return ids;
  }

}
